import { useState, useEffect, useCallback, useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { createSelector } from 'reselect';

const useGroupProgress = (identifier, setValue) => {
  const [completedPercentage, setCompletedPercentage] = useState(0);

  const [childFields, setChildFields] = useState({});

  const selectChildRules = useMemo(
    () => createSelector(
      (state) => state.form.rules,
      (rules) => {
        const nextChildRules = {};

        Object.keys(rules).forEach((fieldIdentifier) => {
          if (fieldIdentifier.startsWith(identifier)) {
            nextChildRules[fieldIdentifier] = rules[fieldIdentifier];
          }
        });

        return nextChildRules;
      }
    ),
    [identifier]
  );

  const childRules = useSelector(selectChildRules, shallowEqual);

  function updateField(childIdentifier, value) {
    setChildFields((prevFields) => ({
      ...prevFields,
      [childIdentifier]: value,
    }));
  }

  useEffect(() => {
    updateCompletedChildren();
  }, [childFields, childRules]);

  const updateCompletedChildren = useCallback(() => {
    let completedChildren = 0;
    let activeChildren = 0;

    Object.keys(childFields).forEach((childIdentifier) => {
      const childIsActive = childRules[childIdentifier]?.visible_expr !== false;

      if (childIsActive) {
        activeChildren += 1;
      }

      const childValue = childFields[childIdentifier];

      if ((childValue || childValue === 0) && childIsActive) {
        completedChildren += 1;
      }
    });

    if (activeChildren === 0) {
      setCompletedPercentage(100);
    } else {
      setCompletedPercentage((100 / activeChildren) * completedChildren);
    }
  }, [identifier, childFields, childRules]);

  return { completedPercentage, updateField };
};

export default useGroupProgress;
